import {
  AdminApiError,
  type AdminClient,
  type WalletCallBulkAcknowledgeResult,
  type WalletDriftBulkResolveResult,
  type WalletDriftEntry,
  type WalletDriftListResult,
  type WalletLedgerEntry,
} from '@apx/admin-sdk';
import { showToast } from '../toast.js';
import { askPrompt, customModal, html } from '../modal.js';
import { fmtTime, fmtDateTime, fmtMoney } from '../format.js';
import { toDecimalString } from '@apx/money';
import { exportXlsx } from '../xlsx.js';

export interface WalletDriftPageDeps {
  client: AdminClient;
  integratorId: string;
}

type DriftStatus = 'unresolved' | 'resolved' | 'all';
type Tab = 'drift' | 'failed';

/**
 * Wallet reconciliation dashboard — the two things that need a human:
 *
 *  - **Drift**: auto-detected mismatches between apx's expected balance
 *    (`prevShadow ± callAmount`) and the integrator-reported balance. Detected,
 *    not enforced — the wallet call still completed. Resolvable per-row or bulk.
 *  - **Failed calls**: wallet callbacks that errored (timeout / 5xx / signature),
 *    surfaced from the wallet-transaction log with `status=failed`.
 *
 * The Home "Reconcile needed" KPI sums both, so this page must show both —
 * previously it only listed drift, which made a non-zero KPI open to an empty
 * table. Tabs default to whichever has the attention (`?view=failed`).
 */
type WindowKey = '24h' | '7d' | '30d' | 'all';

const WINDOWS: Array<{ key: WindowKey; label: string; ms: number | null }> = [
  { key: '24h', label: 'Last 24 hours', ms: 86_400_000 },
  { key: '7d', label: 'Last 7 days', ms: 7 * 86_400_000 },
  { key: '30d', label: 'Last 30 days', ms: 30 * 86_400_000 },
  { key: 'all', label: 'All time', ms: null },
];

/** `from` for the selected window, or nothing at all for "All time". */
function windowRange(key: WindowKey): { from?: string } {
  const win = WINDOWS.find((w) => w.key === key);
  if (!win || win.ms === null) return {};
  return { from: new Date(Date.now() - win.ms).toISOString() };
}

function windowSelect(id: string, selected: WindowKey): string {
  return `<select id="${id}" class="players-filter-select" title="Time window">
    ${WINDOWS.map(
      (w) => `<option value="${w.key}"${w.key === selected ? ' selected' : ''}>${w.label}</option>`,
    ).join('')}
  </select>`;
}

export async function renderWalletDriftPage(
  root: HTMLElement,
  deps: WalletDriftPageDeps,
): Promise<void> {
  const urlParams = new URLSearchParams(window.location.search);
  let tab: Tab = urlParams.get('view') === 'failed' ? 'failed' : 'drift';
  let status: DriftStatus = 'unresolved';
  /**
   * Both tabs are append-only logs that only ever grow, so an all-time default
   * greeted every operator with every problem the integration has ever had —
   * mostly long-since dealt with. 24h matches the other data views and makes
   * the count on screen mean "needs attention now".
   */
  let windowKey: WindowKey = urlParams.get('window') === 'all' ? 'all' : (urlParams.get('window') as WindowKey) || '24h';
  // One free-text box: player ref / username / uuid, surfacing request id,
  // or wallet-tx id. Seeded from a ?playerId= or ?q= deep-link.
  let searchTerm = urlParams.get('q') ?? urlParams.get('playerId') ?? '';

  root.innerHTML = `
    <h1 class="page-title">Wallet reconciliation</h1>
    <p class="page-sub">
      Two things that may need a human. Showing the last 24 hours by default —
      widen the window to see older entries.
    </p>
    <div class="card wr-legend">
      <div>
        <strong>Balance drift</strong> — after a wallet call, the balance your
        wallet reported back did not match what ΛPX expected
        (previous balance ± the amount of that call). The call itself still
        went through; this only flags that the two sides disagree.
        <em>Unresolved</em> means nobody has looked at it yet. Resolving a row
        records that an operator checked it and moves no money.
      </div>
      <div>
        <strong>Failed calls</strong> — a wallet callback ΛPX sent that did not
        succeed: a timeout, a 5xx from your endpoint, or a signature mismatch.
        <em>Failed</em> is the row's own status, not a judgement about the bet.
        Replay re-sends the call; acknowledge just marks it triaged and leaves
        it replayable.
      </div>
    </div>

    <div id="wr-stats" class="players-stats"></div>

    <div class="rtp-subtabs jp-seg" id="wr-tabs" style="margin:4px 0 16px">
      <button class="rtp-subtab" data-tab="drift">Balance drift</button>
      <button class="rtp-subtab" data-tab="failed">Failed calls</button>
    </div>

    <div id="wr-panel"></div>
  `;

  const statsEl = root.querySelector<HTMLElement>('#wr-stats')!;
  const tabsEl = root.querySelector<HTMLElement>('#wr-tabs')!;
  const panelEl = root.querySelector<HTMLElement>('#wr-panel')!;

  // Cached counts for the stat cards (refreshed whenever either tab loads).
  let unresolvedDriftCount: number | null = null;
  let failedCallCount: number | null = null;

  function renderStats(): void {
    const card = (
      label: string,
      value: string,
      opts: { color?: string; sub?: string; primary?: boolean } = {},
    ): string =>
      `<div class="stat-card${opts.primary ? ' is-primary' : ''}"><div class="stat-card-label">${label}</div><div class="stat-card-value"${opts.color ? ` style="color:${opts.color}"` : ''}>${value}</div>${opts.sub ? `<div class="stat-card-sub">${opts.sub}</div>` : ''}</div>`;
    const driftVal = unresolvedDriftCount ?? '—';
    const failedVal = failedCallCount ?? '—';
    const total =
      typeof unresolvedDriftCount === 'number' && typeof failedCallCount === 'number'
        ? unresolvedDriftCount + failedCallCount
        : null;
    statsEl.innerHTML = [
      card('Reconcile needed', total === null ? '—' : String(total), {
        color: total && total > 0 ? 'var(--warn)' : 'var(--success)',
        sub: 'drift + failed calls',
        primary: true,
      }),
      card('Unresolved drift', String(driftVal), {
        color: unresolvedDriftCount && unresolvedDriftCount > 0 ? 'var(--warn)' : undefined,
        // A count that silently described a different range than the table
        // under it is worse than no count, so the tile names its window — and
        // says when older unresolved rows exist outside it.
        sub:
          typeof unresolvedBacklog === 'number' &&
          typeof unresolvedDriftCount === 'number' &&
          unresolvedBacklog > unresolvedDriftCount
            ? `in this window · ${unresolvedBacklog} unresolved in total`
            : 'balance mismatches in this window',
      }),
      card('Failed calls', String(failedVal), {
        color: failedCallCount && failedCallCount > 0 ? 'var(--warn)' : undefined,
        sub: 'unacknowledged in this window',
      }),
    ].join('');
  }

  function paintTabs(): void {
    tabsEl.querySelectorAll<HTMLButtonElement>('.rtp-subtab').forEach((b) => {
      const t = b.dataset.tab as Tab;
      b.classList.toggle('active', t === tab);
      const count = t === 'drift' ? unresolvedDriftCount : failedCallCount;
      const base = t === 'drift' ? 'Balance drift' : 'Failed calls';
      b.textContent = typeof count === 'number' ? `${base} (${count})` : base;
    });
  }

  tabsEl.querySelectorAll<HTMLButtonElement>('.rtp-subtab').forEach((btn) => {
    btn.addEventListener('click', () => {
      tab = btn.dataset.tab as Tab;
      paintTabs();
      void renderTab();
    });
  });

  async function renderTab(): Promise<void> {
    paintTabs();
    if (tab === 'drift') await renderDriftTab();
    else await renderFailedTab();
  }

  // ── Drift tab ──────────────────────────────────────────────────────────────
  let currentUnresolvedCount = 0;
  /** Unresolved across all time, so a narrow window never hides a backlog. */
  let unresolvedBacklog: number | null = null;
  let currentVisibleEntries: WalletDriftEntry[] = [];

  async function renderDriftTab(): Promise<void> {
    panelEl.innerHTML = `
      <div class="card" style="margin-bottom:14px">
        <div class="row players-filters">
          ${windowSelect('dr-window', windowKey)}
          <select id="dr-status" class="players-filter-select" title="Status">
            <option value="unresolved"${status === 'unresolved' ? ' selected' : ''}>Unresolved</option>
            <option value="resolved"${status === 'resolved' ? ' selected' : ''}>Resolved</option>
            <option value="all"${status === 'all' ? ' selected' : ''}>All</option>
          </select>
          <input id="dr-search" type="search" class="players-search-input"
            placeholder="Search player, username, request id, or txn id…"
            value="${escapeHtml(searchTerm)}" />
          <button id="dr-search-run" class="primary">Search</button>
          ${searchTerm ? '<button id="dr-search-clear" class="ghost">Clear</button>' : ''}
          <button id="dr-refresh" class="ghost" title="Reload">Refresh</button>
          <button id="dr-export" class="ghost" title="Export visible rows to Excel (.xlsx)">Export Excel</button>
          <button id="dr-resolve-all" class="danger" style="margin-left:auto" disabled title="Resolves all currently-unresolved rows in this view">Resolve all</button>
        </div>
      </div>
      <div id="dr-body"><p class="muted">Loading…</p></div>
    `;
    const bodyEl = panelEl.querySelector<HTMLElement>('#dr-body')!;
    const resolveAllBtn = panelEl.querySelector<HTMLButtonElement>('#dr-resolve-all')!;

    async function refresh(): Promise<void> {
      bodyEl.innerHTML = '<p class="muted">Loading…</p>';
      try {
        const res = await deps.client.listWalletDrift(deps.integratorId, {
          status,
          ...windowRange(windowKey),
          ...(searchTerm ? { search: searchTerm } : {}),
        });
        bodyEl.innerHTML = renderDriftTable(res);
        bindRowActions(bodyEl);
        currentUnresolvedCount = res.unresolvedCount;
        unresolvedBacklog = res.unresolvedCountAllTime ?? null;
        currentVisibleEntries = res.entries;
        unresolvedDriftCount = res.unresolvedCount;
        renderStats();
        paintTabs();
        const eligible = res.entries.filter((e) => !e.resolvedAt).map((e) => e.id);
        resolveAllBtn.disabled = eligible.length === 0;
        resolveAllBtn.textContent = eligible.length > 0 ? `Resolve all (${eligible.length})` : 'Resolve all';
      } catch (err) {
        bodyEl.innerHTML = `<p class="error">${escapeHtml(describe(err))}</p>`;
        resolveAllBtn.disabled = true;
      }
    }

    function bindRowActions(scope: HTMLElement): void {
      scope.querySelectorAll<HTMLButtonElement>('.dr-resolve').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const driftId = btn.dataset.id!;
          const note = await askPrompt('Resolution note (visible in audit log):');
          if (!note || note.trim().length === 0) return;
          try {
            await deps.client.resolveWalletDrift(driftId, note.trim());
            showToast('Drift marked resolved.', 'success');
            await refresh();
          } catch (err) {
            showToast(describe(err), 'error');
          }
        });
      });
    }

    const searchEl = panelEl.querySelector<HTMLInputElement>('#dr-search')!;
    function runSearch(): void {
      const next = searchEl.value.trim();
      if (next === searchTerm) {
        void refresh();
        return;
      }
      searchTerm = next;
      void renderDriftTab(); // re-render so the Clear button appears/hides
    }

    panelEl.querySelector<HTMLSelectElement>('#dr-window')!.addEventListener('change', (e) => {
      windowKey = (e.target as HTMLSelectElement).value as WindowKey;
      void refresh();
    });
    panelEl.querySelector<HTMLSelectElement>('#dr-status')!.addEventListener('change', (e) => {
      status = (e.target as HTMLSelectElement).value as DriftStatus;
      void refresh();
    });
    searchEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') runSearch();
    });
    panelEl.querySelector<HTMLButtonElement>('#dr-search-run')!.addEventListener('click', () => runSearch());
    panelEl.querySelector<HTMLButtonElement>('#dr-search-clear')?.addEventListener('click', () => {
      searchTerm = '';
      void renderDriftTab();
    });
    panelEl.querySelector<HTMLButtonElement>('#dr-refresh')!.addEventListener('click', () => void refresh());
    panelEl.querySelector<HTMLButtonElement>('#dr-export')!.addEventListener('click', () => {
      if (currentVisibleEntries.length === 0) {
        showToast('No drift rows to export.', 'warn');
        return;
      }
      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      exportXlsx(
        `apx-wallet-drift-${stamp}.xlsx`,
        currentVisibleEntries.map((d) => ({
          id: d.id,
          detectedAt: d.detectedAt,
          integratorPlayerRef: d.integratorPlayerRef,
          playerUsername: d.playerUsername ?? '',
          // Decimals in the player's currency ("1234.56" in USD).
          currency: d.currency,
          expectedCredits: toDecimalString(d.expectedCredits, d.currency),
          integratorReportedCredits: toDecimalString(d.integratorReportedCredits, d.currency),
          delta: toDecimalString(d.delta, d.currency),
          surfacingType: d.walletTxSummary?.type ?? '',
          surfacingReason: d.walletTxSummary?.reason ?? '',
          resolvedAt: d.resolvedAt ?? '',
          resolutionNote: d.resolutionNote ?? '',
        })),
      );
      showToast(`Exported ${currentVisibleEntries.length} drift rows.`, 'success');
    });
    resolveAllBtn.addEventListener('click', () => void runBulkResolve(refresh, resolveAllBtn));

    await refresh();
  }

  function renderDriftTable(res: WalletDriftListResult): string {
    if (res.entries.length === 0) {
      return `<div class="card"><p class="muted">No drift rows match. Either reconciliation is clean, or the filter is too tight.</p></div>`;
    }
    return `
      <div class="card" style="overflow:auto">
        <table>
          <thead>
            <tr>
              <th>Detected</th>
              <th>Player</th>
              <th style="text-align:right">Expected</th>
              <th style="text-align:right">Reported</th>
              <th style="text-align:right">Δ</th>
              <th>Surfacing call</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>${res.entries.map(renderDriftRow).join('')}</tbody>
        </table>
      </div>
    `;
  }

  function renderDriftRow(d: WalletDriftEntry): string {
    const delta = BigInt(d.delta);
    const deltaColor = delta === 0n ? '' : delta > 0n ? 'var(--accent-2)' : 'var(--warn)';
    const resolved = Boolean(d.resolvedAt);
    return `
      <tr data-drift-id="${d.id}">
        <td><span title="${d.detectedAt}">${fmtTime(d.detectedAt)}</span></td>
        <td>
          <div><code style="font-size:11px">${escapeHtml(d.integratorPlayerRef)}</code></div>
          <div class="muted" style="font-size:11px">${escapeHtml(d.playerUsername ?? '')}</div>
        </td>
        <td style="text-align:right;font-variant-numeric:tabular-nums">${fmtMoney(d.expectedCredits, d.currency)} <span class="muted" style="font-size:11px">${escapeHtml(d.currency ?? '')}</span></td>
        <td style="text-align:right;font-variant-numeric:tabular-nums">${fmtMoney(d.integratorReportedCredits, d.currency)}</td>
        <td style="text-align:right;font-variant-numeric:tabular-nums;color:${deltaColor};font-weight:600">${fmtMoney(d.delta, d.currency, { signed: true })}</td>
        <td>
          ${d.walletTxSummary ? `<code style="font-size:11px">${escapeHtml(d.walletTxSummary.type)}</code><div class="muted" style="font-size:11px">${escapeHtml(d.walletTxSummary.reason)}</div>` : '<span class="muted">—</span>'}
        </td>
        <td>
          ${resolved
            ? `<span class="pill live">resolved</span><div class="muted" style="font-size:11px;margin-top:2px;max-width:200px">${escapeHtml(d.resolutionNote ?? '')}</div>`
            : '<span class="pill warn">unresolved</span>'}
        </td>
        <td style="text-align:right">
          ${resolved ? '' : `<button class="dr-resolve" data-id="${d.id}">Resolve…</button>`}
        </td>
      </tr>
    `;
  }

  // ── Failed-calls tab ───────────────────────────────────────────────────────
  let failedVisibleEntries: WalletLedgerEntry[] = [];

  async function renderFailedTab(): Promise<void> {
    panelEl.innerHTML = `
      <div class="card" style="margin-bottom:14px">
        <div class="row" style="gap:14px;flex-wrap:wrap;align-items:flex-end">
          <p class="muted" style="margin:0;font-size:12px;max-width:660px">
            Wallet callbacks to this integrator that errored (timeout, 5xx, non-JSON
            body, or a rejected signature). The bet/round still settled on apx's
            side. <strong>Acknowledge</strong> clears a row from this list once
            you've triaged which endpoint/integrator to fix — it does not reconcile
            balances (see Balance drift) or re-fire the call.
          </p>
          ${windowSelect('fc-window', windowKey)}
          <div class="row" style="gap:8px;margin:0 0 0 auto">
            <button id="fc-export" class="ghost" title="Export to Excel (.xlsx)">Export Excel</button>
            <button id="fc-refresh" class="ghost">Refresh</button>
            <button id="fc-ack-all" class="primary" disabled title="Acknowledge all failed calls currently in this list">Acknowledge all</button>
          </div>
        </div>
      </div>
      <div id="fc-body"><p class="muted">Loading…</p></div>
    `;
    const bodyEl = panelEl.querySelector<HTMLElement>('#fc-body')!;
    const ackAllBtn = panelEl.querySelector<HTMLButtonElement>('#fc-ack-all')!;
    let failedEntries: WalletLedgerEntry[] = [];

    async function refresh(): Promise<void> {
      bodyEl.innerHTML = '<p class="muted">Loading…</p>';
      try {
        const res = await deps.client.listWalletTransactions(deps.integratorId, {
          status: 'failed',
          acknowledged: 'false',
          ...windowRange(windowKey),
          limit: 200,
        });
        failedEntries = res.entries;
        failedVisibleEntries = res.entries;
        failedCallCount = res.entries.length;
        renderStats();
        paintTabs();
        bodyEl.innerHTML = renderFailedTable(res.entries);
        bindFailedRowActions(bodyEl, refresh);
        ackAllBtn.disabled = res.entries.length === 0;
        ackAllBtn.textContent =
          res.entries.length > 0 ? `Acknowledge all (${res.entries.length})` : 'Acknowledge all';
      } catch (err) {
        bodyEl.innerHTML = `<p class="error">${escapeHtml(describe(err))}</p>`;
        ackAllBtn.disabled = true;
      }
    }

    function bindFailedRowActions(scope: HTMLElement, reload: () => Promise<void>): void {
      scope.querySelectorAll<HTMLButtonElement>('.fc-ack').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const txId = btn.dataset.id!;
          const note = await askPrompt('Acknowledgement note (visible in audit log):');
          if (!note || note.trim().length === 0) return;
          btn.disabled = true;
          try {
            await deps.client.acknowledgeWalletCall(txId, note.trim());
            showToast('Failed call acknowledged.', 'success');
            await reload();
          } catch (err) {
            showToast(describe(err), 'error');
            btn.disabled = false;
          }
        });
      });
    }

    panelEl.querySelector<HTMLSelectElement>('#fc-window')!.addEventListener('change', (e) => {
      windowKey = (e.target as HTMLSelectElement).value as WindowKey;
      void refresh();
    });
    panelEl.querySelector<HTMLButtonElement>('#fc-refresh')!.addEventListener('click', () => void refresh());
    ackAllBtn.addEventListener('click', () => void runBulkAck(refresh, ackAllBtn));
    panelEl.querySelector<HTMLButtonElement>('#fc-export')!.addEventListener('click', () => {
      if (failedEntries.length === 0) {
        showToast('No failed calls to export.', 'warn');
        return;
      }
      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      exportXlsx(
        `apx-failed-wallet-calls-${stamp}.xlsx`,
        failedEntries.map((e) => ({
          id: e.id,
          createdAt: e.createdAt,
          playerId: e.playerId,
          playerUsername: e.playerUsername ?? '',
          type: e.type,
          // Decimals in the player's currency ("1234.56" in USD).
          currency: e.currency || e.fiatCurrency,
          creditAmount: toDecimalString(e.creditAmount, e.currency || e.fiatCurrency),
          fiatAmount: e.fiatAmount == null ? '' : toDecimalString(e.fiatAmount, e.fiatCurrency || e.currency),
          fiatCurrency: e.fiatCurrency,
          attempt: e.attempt ?? '',
          latencyMs: e.latencyMs ?? '',
          lastError: e.lastError ?? '',
          requestId: e.requestId,
          refTxId: e.refTxId ?? '',
        })),
      );
      showToast(`Exported ${failedEntries.length} failed calls.`, 'success');
    });
    await refresh();
  }

  function renderFailedTable(entries: WalletLedgerEntry[]): string {
    if (entries.length === 0) {
      return `<div class="card"><p class="muted">No failed wallet calls. Every callback to this integrator has been accepted.</p></div>`;
    }
    const gh = (sub: string): string => `/integrators/${encodeURIComponent(deps.integratorId)}/${sub}`;
    return `
      <div class="card" style="overflow:auto">
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Player</th>
              <th>Type</th>
              <th style="text-align:right">Amount</th>
              <th style="text-align:right">Attempt</th>
              <th style="text-align:right">Latency</th>
              <th>Error</th>
              <th>Refs</th>
              <th></th>
            </tr>
          </thead>
          <tbody>${entries.map((e) => renderFailedRow(e, gh)).join('')}</tbody>
        </table>
      </div>
    `;
  }

  function renderFailedRow(e: WalletLedgerEntry, gh: (sub: string) => string): string {
    const player = e.playerIsAnonymous
      ? '<span class="muted">anonymous</span>'
      : escapeHtml(e.playerUsername ?? e.playerId.slice(0, 8) + '…');
    // Native model: money moves 1:1, so creditAmount is the amount in the
    // player's currency. fiatAmount is a legacy mirror (0/absent on
    // pre-native rows) and must not mask the real amount in the triage list —
    // a failed 58,500 payout was showing as "0 IDR" when it read fiatAmount.
    const cur = e.currency || e.fiatCurrency;
    const amount = `${fmtMoney(e.creditAmount, cur)} <span class="muted" style="font-size:11px">${escapeHtml(cur ?? '')}</span>`;
    const refLink = e.refTxId
      ? `<a href="${gh(`transaction-trace?id=${encodeURIComponent(e.refTxId)}`)}" title="Open trace"><code style="font-size:11px">${escapeHtml(e.refTxId.slice(-8))}</code></a>`
      : '<span class="muted">—</span>';
    return `
      <tr>
        <td><span title="${e.createdAt}">${fmtDateTime(e.createdAt)}</span></td>
        <td>${player}</td>
        <td><code style="font-size:11px">${escapeHtml(e.type)}</code><div class="muted" style="font-size:11px">${escapeHtml(e.reason)}</div></td>
        <td style="text-align:right;font-variant-numeric:tabular-nums">${amount}</td>
        <td style="text-align:right">${e.attempt != null ? `<span class="pill warn">×${e.attempt}</span>` : '<span class="muted">—</span>'}</td>
        <td style="text-align:right;font-variant-numeric:tabular-nums">${e.latencyMs != null ? `${e.latencyMs}ms` : '<span class="muted">—</span>'}</td>
        <td style="max-width:280px"><span class="error" style="font-size:11px">${escapeHtml(e.lastError ?? 'unknown error')}</span></td>
        <td style="font-size:11px">
          <div>req <code style="font-size:10px">${escapeHtml(e.requestId.slice(-8))}</code></div>
          <div>ref ${refLink}</div>
        </td>
        <td style="text-align:right"><button class="fc-ack" data-id="${e.id}">Acknowledge…</button></td>
      </tr>
    `;
  }

  // ── Bulk resolve (drift only) ──────────────────────────────────────────────
  async function runBulkResolve(
    refresh: () => Promise<void>,
    resolveAllBtn: HTMLButtonElement,
  ): Promise<void> {
    const visibleIds = currentVisibleEntries.filter((e) => !e.resolvedAt).map((e) => e.id);
    if (visibleIds.length === 0) return;
    const filtersActive = Boolean(currentUnresolvedCount && searchTerm);
    const scopeNote = filtersActive
      ? html`<p class="modal-body" style="margin:8px 0 4px">The active search narrows the scope — only the <strong>${visibleIds.length}</strong> rows currently visible will be resolved. Total integrator-wide unresolved: <strong>${currentUnresolvedCount}</strong>.</p>`
      : html`<p class="modal-body" style="margin:8px 0 4px">This will resolve up to <strong>500</strong> unresolved drift rows for this integrator at once (currently <strong>${currentUnresolvedCount}</strong>).</p>`;
    const parsed = await customModal<{ note: string; ids: string[] }>({
      title: 'Bulk-resolve drift',
      body: html`
        ${scopeNote}
        <label style="display:flex;flex-direction:column;gap:4px;margin-top:8px">
          <span class="muted" style="font-size:11px">resolution note (shared by all rows, visible in audit log)</span>
          <input id="dr-bulk-note" placeholder="e.g. integrator reconciled batch 2026-05-29" style="min-width:380px" />
        </label>
      `,
      confirmLabel: `Resolve ${filtersActive ? visibleIds.length : 'all'}`,
      danger: true,
      parseOnConfirm: (card) => {
        const note = (card.querySelector<HTMLInputElement>('#dr-bulk-note')?.value ?? '').trim();
        if (!note) return null;
        return { note, ids: filtersActive ? visibleIds : [] };
      },
    });
    if (!parsed) return;
    resolveAllBtn.disabled = true;
    resolveAllBtn.textContent = 'Resolving…';
    try {
      const result = await deps.client.bulkResolveWalletDrift(deps.integratorId, {
        resolutionNote: parsed.note,
        ...(parsed.ids.length > 0 ? { ids: parsed.ids } : {}),
      });
      await refresh();
      await showBulkSummary(result);
    } catch (err) {
      showToast(describe(err), 'error');
      resolveAllBtn.disabled = false;
    }
  }

  async function showBulkSummary(result: WalletDriftBulkResolveResult): Promise<void> {
    const okCount = result.succeeded.length;
    const failCount = result.failed.length;
    const tone = failCount === 0 ? 'success' : okCount === 0 ? 'error' : 'warn';
    if (failCount === 0) {
      showToast(
        `Bulk-resolved ${okCount} drift row${okCount === 1 ? '' : 's'} in ${result.elapsedMs} ms.`,
        'success',
      );
      return;
    }
    const failuresHtml = result.failed.slice(0, 50).map(
      (f) => html`
          <tr>
            <td><code style="font-size:11px">${f.id.slice(0, 8)}…</code></td>
            <td><span class="pill ${f.code === 'ALREADY_RESOLVED' ? 'live' : 'warn'}">${f.code}</span></td>
            <td class="muted" style="font-size:12px">${f.message}</td>
          </tr>`,
    );
    const moreNote =
      result.failed.length > 50
        ? html`<p class="muted" style="font-size:11px;margin-top:6px">…and ${result.failed.length - 50} more not shown.</p>`
        : '';
    const remainingNote =
      result.remainingUnresolved > 0 && result.totalAttempted >= result.cappedAt
        ? html`<p class="muted" style="font-size:12px;margin-top:8px">Server caps each batch at ${result.cappedAt}. ${result.remainingUnresolved} unresolved rows remain — click Resolve all again to continue.</p>`
        : result.remainingUnresolved > 0
          ? html`<p class="muted" style="font-size:12px;margin-top:8px">${result.remainingUnresolved} unresolved rows remain (likely outside current filter).</p>`
          : '';
    await customModal({
      title: tone === 'error' ? 'Bulk-resolve failed' : 'Bulk-resolve summary',
      body: html`
        <div class="row" style="gap:14px;margin:4px 0 10px">
          <div><span class="muted">Attempted</span> <strong>${result.totalAttempted}</strong></div>
          <div><span class="muted">Resolved</span> <strong style="color:var(--accent-2)">${okCount}</strong></div>
          <div><span class="muted">Failed</span> <strong style="color:var(--warn)">${failCount}</strong></div>
          <div class="muted" style="margin-left:auto;font-size:12px">${result.elapsedMs} ms</div>
        </div>
        <div class="card" style="max-height:280px;overflow:auto;padding:6px 8px">
          <table style="width:100%">
            <thead>
              <tr><th>Drift id</th><th>Code</th><th>Reason</th></tr>
            </thead>
            <tbody>${failuresHtml}</tbody>
          </table>
        </div>
        ${moreNote}
        ${remainingNote}
      `,
      confirmLabel: 'Close',
      cancelLabel: 'Dismiss',
      parseOnConfirm: () => ({}),
    });
  }

  // ── Bulk acknowledge (failed calls) ────────────────────────────────────────
  async function runBulkAck(
    refresh: () => Promise<void>,
    ackAllBtn: HTMLButtonElement,
  ): Promise<void> {
    const count = failedVisibleEntries.length;
    if (count === 0) return;
    const parsed = await customModal<{ note: string }>({
      title: 'Acknowledge failed calls',
      body: html`
        <p class="modal-body" style="margin:8px 0 4px">
          Clears up to <strong>500</strong> unacknowledged failed calls for this
          integrator at once (currently <strong>${count}</strong> in view). Triage
          only — it does not reconcile balances or re-fire the calls.
        </p>
        <label style="display:flex;flex-direction:column;gap:4px;margin-top:8px">
          <span class="muted" style="font-size:11px">note (shared by all rows, visible in audit log)</span>
          <input id="fc-bulk-note" placeholder="e.g. broken wallet URL — integrator notified" style="min-width:380px" />
        </label>
      `,
      confirmLabel: 'Acknowledge all',
      parseOnConfirm: (card) => {
        const note = (card.querySelector<HTMLInputElement>('#fc-bulk-note')?.value ?? '').trim();
        if (!note) return null;
        return { note };
      },
    });
    if (!parsed) return;
    ackAllBtn.disabled = true;
    ackAllBtn.textContent = 'Acknowledging…';
    try {
      const result = await deps.client.acknowledgeAllWalletCalls(deps.integratorId, {
        note: parsed.note,
      });
      await refresh();
      await showAckSummary(result);
    } catch (err) {
      showToast(describe(err), 'error');
      ackAllBtn.disabled = false;
    }
  }

  async function showAckSummary(result: WalletCallBulkAcknowledgeResult): Promise<void> {
    const okCount = result.succeeded.length;
    const failCount = result.failed.length;
    if (failCount === 0) {
      showToast(
        `Acknowledged ${okCount} failed call${okCount === 1 ? '' : 's'} in ${result.elapsedMs} ms.`,
        'success',
      );
      return;
    }
    const tone = okCount === 0 ? 'error' : 'warn';
    const failuresHtml = result.failed.slice(0, 50).map(
      (f) => html`
          <tr>
            <td><code style="font-size:11px">${f.id.slice(0, 8)}…</code></td>
            <td><span class="pill ${f.code === 'ALREADY_ACKNOWLEDGED' ? 'live' : 'warn'}">${f.code}</span></td>
            <td class="muted" style="font-size:12px">${f.message}</td>
          </tr>`,
    );
    const moreNote =
      result.failed.length > 50
        ? html`<p class="muted" style="font-size:11px;margin-top:6px">…and ${result.failed.length - 50} more not shown.</p>`
        : '';
    const remainingNote =
      result.remainingUnacknowledged > 0 && result.totalAttempted >= result.cappedAt
        ? html`<p class="muted" style="font-size:12px;margin-top:8px">Server caps each batch at ${result.cappedAt}. ${result.remainingUnacknowledged} unacknowledged rows remain — click Acknowledge all again to continue.</p>`
        : result.remainingUnacknowledged > 0
          ? html`<p class="muted" style="font-size:12px;margin-top:8px">${result.remainingUnacknowledged} unacknowledged rows remain.</p>`
          : '';
    await customModal({
      title: tone === 'error' ? 'Acknowledge failed' : 'Acknowledge summary',
      body: html`
        <div class="row" style="gap:14px;margin:4px 0 10px">
          <div><span class="muted">Attempted</span> <strong>${result.totalAttempted}</strong></div>
          <div><span class="muted">Acknowledged</span> <strong style="color:var(--accent-2)">${okCount}</strong></div>
          <div><span class="muted">Failed</span> <strong style="color:var(--warn)">${failCount}</strong></div>
          <div class="muted" style="margin-left:auto;font-size:12px">${result.elapsedMs} ms</div>
        </div>
        <div class="card" style="max-height:280px;overflow:auto;padding:6px 8px">
          <table style="width:100%">
            <thead>
              <tr><th>Call id</th><th>Code</th><th>Reason</th></tr>
            </thead>
            <tbody>${failuresHtml}</tbody>
          </table>
        </div>
        ${moreNote}
        ${remainingNote}
      `,
      confirmLabel: 'Close',
      cancelLabel: 'Dismiss',
      parseOnConfirm: () => ({}),
    });
  }

  // ── Init ─────────────────────────────────────────────────────────────────
  renderStats();
  // Warm the other tab's count in the background so both badges show immediately.
  void deps.client
    .listWalletTransactions(deps.integratorId, {
      status: 'failed',
      acknowledged: 'false',
      ...windowRange(windowKey),
      limit: 200,
    })
    .then((r) => {
      failedCallCount = r.entries.length;
      renderStats();
      paintTabs();
    })
    .catch(() => {});
  await renderTab();
}

function describe(err: unknown): string {
  if (err instanceof AdminApiError) return `${err.code}: ${err.message}`;
  return err instanceof Error ? err.message : String(err);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}
